import { Icon } from "@iconify/react/dist/iconify.js";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type FormEvent, useId, useState, useTransition } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";
import { requestPasswordRecovery } from "$/modules/auth/lib/auth";
import { AuthLayout } from "$/modules/auth/ui/auth-layout";
import { Button } from "$/modules/shared/ui/button";
import { Field, FieldLabel } from "$/modules/shared/ui/field";
import { Input } from "$/modules/shared/ui/input";
import { Spinner } from "$/modules/shared/ui/spinner";

export const Route = createFileRoute("/$locale/forgot-password/")({
	component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
	const fieldId = useId();

	const [email, setEmail] = useState("");
	const [sent, setSent] = useState(false);
	const t = useTranslations();
	const { locale } = Route.useParams();
	const [isPending, startTransition] = useTransition();

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault();

		startTransition(async () => {
			const redirectUrl = `${window.location.origin}/${locale}/recovery`;
			const response = await requestPasswordRecovery(email, redirectUrl);
			if (!response.success) {
				toast.error(response.message, {});
				return;
			}
			setSent(true);
		});
	};

	return (
		<AuthLayout
			locale={locale}
			title={t("auth.forgotTitle")}
			subtitle={t("auth.forgotDescription")}
			footer={
				<Link
					to="/$locale/signin"
					params={{ locale }}
					className="font-semibold text-primary hover:underline"
				>
					{t("auth.backToSignIn")}
				</Link>
			}
		>
			{sent ? (
				<div className="flex flex-col items-center gap-3 py-4 text-center">
					<span className="flex size-12 items-center justify-center rounded-full bg-success/10 text-success">
						<Icon icon="lucide:mail-check" className="size-6" />
					</span>
					<p className="text-foreground-600">{t("auth.forgotSent")}</p>
				</div>
			) : (
				<form onSubmit={handleSubmit} className="space-y-4">
					<Field>
						<FieldLabel htmlFor={`${fieldId}-100`}>{t("email")}</FieldLabel>
						<Input
							id={`${fieldId}-100`}
							autoComplete="email"
							type="email"
							placeholder={t("emailPlaceholder")}
							value={email}
							required
							onChange={(event) => setEmail(event.currentTarget.value)}
						/>
					</Field>
					<Button
						type="submit"
						size="lg"
						className="w-full font-semibold"
						disabled={isPending}
						variant="default"
					>
						{isPending && <Spinner data-icon="inline-start" />}
						{t("auth.sendRecovery")}
					</Button>
				</form>
			)}
		</AuthLayout>
	);
}
